import { useState, useEffect, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  Policy,
  PolicyType,
  PolicyTransactionType,
  PolicyLimitation,
  OrganizationToken,
} from '@mls-wallet/shared-types';
import { MemberGroupSelector } from '@/components/ui/member-group-selector';
import type { SelectableItem } from '@/components/ui/member-group-selector';
import { AccountSelector } from '@/components/ui/account-selector';
import {
  DestinationAddressSelector,
  type DestinationAddressSelectorValue,
} from './destination-address-selector';
import {
  ContractAddressSelector,
  type ContractAddressSelectorValue,
} from './contract-address-selector';
import {
  TokenSelector,
  TokenSelectorValue,
} from '@/components/ui/token-selector';
import {
  FunctionSelector,
  type FunctionSelectorValue,
} from './function-selector';
import { LimitationSelector } from './limitation-selector';
import {
  Section,
  SectionLeftColumn,
  SectionRightColumn,
  SectionRow,
  SectionTitle,
  SectionDescription,
} from '@/components/shared/SectionComponents';
import {
  Account,
  ContractAbiDetailsErrorCode,
  NetworkType,
  NETWORK_OPTIONS,
  createNetworkId,
  ContractAbiDetailsType,
} from '@mls-wallet/shared-types';
import { useOrganizationTokens } from '@/hooks/organization/use-organization-tokens';
import { useContractAbi } from '@/hooks/contract/use-contract-abi';
import { toHumanReadableTokenAmount } from '@/utils/tokenAmountUtils';

interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  type: 'individual';
}

interface Group {
  id: string;
  name: string;
  memberCount: number;
  type: 'group';
}

// Filter network options to those represented by organization accounts
function getAvailablePolicyNetworks(accounts: Account[]) {
  const organizationNetworkIds = new Set(
    accounts.flatMap((account) => account.networks)
  );

  return NETWORK_OPTIONS.filter((network) =>
    organizationNetworkIds.has(network.id)
  );
}

// Form's internal state - passed to parent via onDataChange
export interface PolicyFormState {
  policyName: string;
  policyDescription: string;
  policyType: PolicyType;
  policyTransactionType: PolicyTransactionType;
  networkId: NetworkType | null;
  selectedAccounts: Account[];
  selectedInitiator: SelectableItem | null;
  selectedApprover: SelectableItem | null;
  approverThreshold: number | null; // For group approvers, how many must approve
  tokenSelection: TokenSelectorValue;
  amountThreshold: string;
  destinationAddressSelection: DestinationAddressSelectorValue;
  contractAddressSelection: ContractAddressSelectorValue;
  functionSelection: FunctionSelectorValue;
  limitation: PolicyLimitation | null;
}

interface PolicyForm2Props {
  initialData?: Policy | undefined;
  onDataChange: (data: PolicyFormState) => void;
  sectionVariant?: 'default' | 'muted';
  accounts: Account[];
  groups: Group[];
  members: OrganizationMember[];
}

export function PolicyForm2({
  initialData,
  onDataChange,
  sectionVariant = 'default',
  accounts,
  groups,
  members,
}: PolicyForm2Props) {
  const [policyName, setPolicyName] = useState('');
  const [policyDescription, setPolicyDescription] = useState('');
  const [policyType, setPolicyType] = useState<PolicyType>('AUTO_APPROVAL');
  const [policyTransactionType, setPolicyTransactionType] =
    useState<PolicyTransactionType>('ANY');
  const [networkId, setNetworkId] = useState<NetworkType | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);

  // initialData means this form is editing an existing policy. Policy networks
  // are immutable after creation, so edit mode locks the selector; create mode
  // has no initialData and can choose the network normally.
  const isNetworkLocked = initialData !== undefined;

  // Filter networks to only show those with organization accounts
  const availableNetworks = useMemo(
    () => getAvailablePolicyNetworks(accounts),
    [accounts]
  );

  // Filter accounts to only show those with addresses on the selected network
  const accountsForNetwork = useMemo(
    () =>
      networkId
        ? accounts.filter((account) =>
            account.networks.some((network) => network === networkId)
          )
        : [],
    [accounts, networkId]
  );

  // Fetch tokens for the selected network and accounts
  const {
    tokens,
    isLoading: isLoadingTokens,
    hasSuccessfulResponse: hasLoadedTokens,
  } = useOrganizationTokens({
    networkId,
    accountIds:
      selectedAccounts.length > 0
        ? selectedAccounts.map((a) => a.id)
        : undefined,
  });

  // Clear selected accounts and token when network changes (but not during initialization)
  useEffect(() => {
    if (!isInitializing.current) {
      setSelectedAccounts([]);
      setTokenSelection({ type: 'any' });
      setLimitation(null);
    }
  }, [networkId]);

  const [selectedInitiator, setSelectedInitiator] =
    useState<SelectableItem | null>(null);
  const [selectedApprover, setSelectedApprover] =
    useState<SelectableItem | null>(null);
  // This is a string because of the <Select> component, default to 1
  const [groupApprovalCount, setGroupApprovalCount] = useState('1');

  useEffect(() => {
    if (policyType === 'AUTO_APPROVAL') {
      setSelectedApprover(null);
      setGroupApprovalCount('1');
    }
  }, [policyType]);

  // Token transfer specific fields
  const [tokenSelection, setTokenSelection] = useState<TokenSelectorValue>({
    type: 'any',
  });
  const [amountThreshold, setAmountThreshold] = useState('');
  const [destinationAddressSelection, setDestinationAddressSelection] =
    useState<DestinationAddressSelectorValue>({ type: 'any' });

  // Successful account-scoped token queries omit assets with no positive
  // persisted balance. Preserve the selected token as an explicit policy
  // choice even when the new accounts do not currently hold it.
  const selectedTokenHasNoBalance =
    hasLoadedTokens &&
    !isLoadingTokens &&
    tokenSelection.type === 'specific' &&
    tokenSelection.token !== undefined &&
    !tokens.some((token) => token.id === tokenSelection.token?.id);
  const tokenOptions = useMemo(() => {
    if (
      tokenSelection.type !== 'specific' ||
      !tokenSelection.token ||
      tokens.some((token) => token.id === tokenSelection.token?.id)
    ) {
      return tokens;
    }

    return [tokenSelection.token, ...tokens];
  }, [tokenSelection, tokens]);

  // Contract interaction specific fields
  const [contractAddressSelection, setContractAddressSelection] =
    useState<ContractAddressSelectorValue>({ type: 'any' });
  const [functionSelection, setFunctionSelection] =
    useState<FunctionSelectorValue>({
      proxyFunctions: [],
      implementationFunctions: [],
    });

  // Clear function selection when contract changes (but not during initialization)
  useEffect(() => {
    if (!isInitializing.current) {
      setFunctionSelection({ proxyFunctions: [], implementationFunctions: [] });
    }
  }, [contractAddressSelection]);

  const contractAbi = useContractAbi(
    contractAddressSelection.type === 'specific' &&
      networkId &&
      contractAddressSelection.address
      ? {
          address: contractAddressSelection.address,
          networkType: networkId,
        }
      : null
  );

  // Limitation specific fields (null = single transaction)
  const [limitation, setLimitation] = useState<PolicyLimitation | null>(null);
  const canConfigureLimitation =
    policyTransactionType === 'TOKEN_TRANSFER' &&
    amountThreshold.trim() !== '' &&
    tokenSelection.type !== 'any';

  useEffect(() => {
    if (policyTransactionType !== 'TOKEN_TRANSFER') {
      setLimitation(null);
      return;
    }
    if (amountThreshold.trim() === '' || tokenSelection.type === 'any') {
      setLimitation(null);
    }
  }, [policyTransactionType, amountThreshold, tokenSelection]);

  // Use ref to track if we're initializing to avoid calling onDataChange during initialization
  const isInitializing = useRef(false);
  const onDataChangeRef = useRef(onDataChange);

  // Update the ref whenever onDataChange changes
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Pre-fill form when initialData is provided
  // Note: initialData is now memoized in parent, so it only changes when policy.id changes
  useEffect(() => {
    if (initialData) {
      isInitializing.current = true;

      setPolicyName(initialData.name);
      setPolicyDescription(initialData.description || '');
      setPolicyType(initialData.type);
      setNetworkId(initialData.networkId);
      setPolicyTransactionType(initialData.transactionType);
      setLimitation(initialData.limitation);

      const matchedAccounts = accounts.filter((acc) =>
        initialData.accounts.some((policyAcc) => policyAcc.id === acc.id)
      );
      setSelectedAccounts(matchedAccounts);

      if (initialData.initiatorSetting) {
        const setting = initialData.initiatorSetting;
        if (setting.type === 'user') {
          const member = members.find((m) => m.id === setting.userId);
          if (member) {
            setSelectedInitiator({ ...member, type: 'individual' });
          }
        } else if (setting.type === 'group') {
          const group = groups.find((g) => g.id === setting.groupId);
          if (group) {
            setSelectedInitiator({ ...group, type: 'group' });
          }
        }
      }

      if (
        initialData.type === 'MANUAL_APPROVAL' &&
        initialData.approverSetting
      ) {
        const setting = initialData.approverSetting;
        if (setting.type === 'user') {
          const member = members.find((m) => m.id === setting.userId);
          if (member) {
            setSelectedApprover({ ...member, type: 'individual' });
          }
        } else if (setting.type === 'group') {
          const group = groups.find((g) => g.id === setting.groupId);
          if (group) {
            setSelectedApprover({ ...group, type: 'group' });
            setGroupApprovalCount(setting.threshold.toString());
          }
        }
      } else {
        setSelectedApprover(null);
        setGroupApprovalCount('1');
      }

      if (
        initialData.transactionType === 'TOKEN_TRANSFER' &&
        'tokenTransferCondition' in initialData
      ) {
        const condition = initialData.tokenTransferCondition;
        if (condition) {
          // Set token selection from enriched condition data
          if (
            condition.tokenId &&
            condition.tokenSymbol &&
            condition.tokenDecimals != null
          ) {
            setTokenSelection({
              type: 'specific',
              token: {
                id: condition.tokenId,
                symbol: condition.tokenSymbol,
                name: condition.tokenName,
                logoUrl: condition.tokenLogoUrl,
                decimals: condition.tokenDecimals,
                address: condition.tokenAddress ?? '',
                networkId: initialData.networkId,
              },
            });

            // Convert raw token units to human-readable for display
            if (condition.amountThreshold) {
              setAmountThreshold(
                toHumanReadableTokenAmount(
                  condition.amountThreshold,
                  condition.tokenDecimals
                )
              );
            } else {
              setAmountThreshold('');
            }
          } else {
            setTokenSelection({ type: 'any' });
            setAmountThreshold('');
          }

          if (
            condition.destinationAddresses &&
            condition.destinationAddresses.length > 0
          ) {
            setDestinationAddressSelection({
              type: 'custom',
              addresses: condition.destinationAddresses,
              ...(condition.destinationAddressEntries?.length
                ? { entries: condition.destinationAddressEntries }
                : {}),
            });
          } else {
            setDestinationAddressSelection({ type: 'any' });
          }
        } else {
          setTokenSelection({ type: 'any' });
          setAmountThreshold('');
          setDestinationAddressSelection({ type: 'any' });
        }
      } else if (
        initialData.transactionType === 'CONTRACT_INTERACTION' &&
        'contractInteractionCondition' in initialData
      ) {
        const condition = initialData.contractInteractionCondition;
        if (condition && condition.length > 0) {
          const addresses = condition.map((c) => c.contractAddress);
          // Keep the full function objects (with name and selector)
          const proxyFunctions = condition[0]?.proxyFunctions || [];
          const implementationFunctions =
            condition[0]?.implementationFunctions || [];
          if (addresses[0]) {
            setContractAddressSelection({
              type: 'specific',
              address: addresses[0],
            });
          } else {
            setContractAddressSelection({ type: 'any' });
          }

          if (proxyFunctions.length > 0 || implementationFunctions.length > 0) {
            setFunctionSelection({
              proxyFunctions,
              implementationFunctions,
            });
          } else {
            setFunctionSelection({
              proxyFunctions: [],
              implementationFunctions: [],
            });
          }
        } else {
          setContractAddressSelection({ type: 'any' });
          setFunctionSelection({
            proxyFunctions: [],
            implementationFunctions: [],
          });
        }
      }

      setTimeout(() => {
        isInitializing.current = false;
      }, 0);
    }
  }, [initialData, accounts, members, groups]);

  useEffect(() => {
    if (!isInitializing.current) {
      onDataChangeRef.current({
        policyName,
        policyDescription,
        policyType,
        policyTransactionType,
        networkId,
        selectedAccounts,
        selectedInitiator,
        selectedApprover,
        approverThreshold: groupApprovalCount
          ? parseInt(groupApprovalCount, 10)
          : null,
        tokenSelection,
        amountThreshold,
        destinationAddressSelection,
        contractAddressSelection,
        functionSelection,
        limitation,
      });
    }
  }, [
    policyName,
    policyDescription,
    policyType,
    policyTransactionType,
    networkId,
    selectedAccounts,
    selectedInitiator,
    selectedApprover,
    groupApprovalCount,
    tokenSelection,
    amountThreshold,
    destinationAddressSelection,
    contractAddressSelection,
    functionSelection,
    limitation,
  ]);

  // Configurable outline color for all form inputs
  const outlineColor = 'border-slate-300 border';

  return (
    <div className="p-6 max-w-3xl mx-auto w-full flex flex-col gap-8">
      {/* Basic info */}
      <Section title="General">
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Policy name</SectionTitle>

            <SectionDescription>
              What you want to name this policy
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Input
                  id="policy-name"
                  value={policyName}
                  onChange={(e) => setPolicyName(e.target.value)}
                  placeholder="Give your policy a name"
                  size="lg"
                  className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                />
              </div>
            </div>
          </SectionRightColumn>
        </SectionRow>

        <Separator />

        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Policy description</SectionTitle>
            <SectionDescription>
              Optional description to explain what this policy does
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Textarea
                  id="policy-description"
                  value={policyDescription}
                  onChange={(e) => setPolicyDescription(e.target.value)}
                  placeholder="Describe the purpose of this policy (optional)"
                  className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                  rows={3}
                />
              </div>
            </div>
          </SectionRightColumn>
        </SectionRow>

        <Separator />

        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Blockchain Network</SectionTitle>
            <SectionDescription>
              Which blockchain network this policy applies to
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <div className="space-y-2">
              <Select
                disabled={isNetworkLocked}
                value={networkId?.toString() ?? ''}
                onValueChange={(value) => {
                  if (isNetworkLocked) return;
                  setNetworkId(createNetworkId(parseInt(value)));
                  setSelectedAccounts([]); // Clear accounts when network changes
                }}
              >
                <SelectTrigger
                  id="network"
                  size="lg"
                  className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                >
                  <SelectValue placeholder="Select a blockchain network" />
                </SelectTrigger>
                <SelectContent>
                  {availableNetworks.map((network) => (
                    <SelectItem key={network.id} value={network.id.toString()}>
                      {network.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </SectionRightColumn>
        </SectionRow>
      </Section>

      {/* Policy type */}
      <Section title="Policy type">
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Policy type</SectionTitle>
            <SectionDescription>
              What happens to transactions that match this policy
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <div className="space-y-2">
              <Select
                value={policyType}
                onValueChange={(value) => setPolicyType(value as PolicyType)}
              >
                <SelectTrigger
                  id="action-type"
                  size="lg"
                  className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                >
                  <SelectValue placeholder="Select an action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUTO_APPROVAL">
                    Automatically approve transactions
                  </SelectItem>
                  <SelectItem value="MANUAL_APPROVAL">
                    Require manual approval
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </SectionRightColumn>
        </SectionRow>

        {policyType === 'MANUAL_APPROVAL' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Transaction approvers</SectionTitle>
                <SectionDescription>
                  {' '}
                  Person or group that should approve transactions
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <MemberGroupSelector
                  {...(selectedApprover ? { value: selectedApprover } : {})}
                  onValueChange={(value) => setSelectedApprover(value ?? null)}
                  placeholder="Select a person or group"
                  size="lg"
                  className={`bg-background text-base ${outlineColor}`}
                  showAnyOption={false}
                  members={members}
                  groups={groups}
                />
              </SectionRightColumn>
            </SectionRow>
          </>
        )}

        {policyType === 'MANUAL_APPROVAL' &&
          selectedApprover?.type === 'group' && (
            <>
              <Separator className="w-full col-span-2" />
              <SectionRow>
                <SectionLeftColumn>
                  <SectionTitle>Approval threshold</SectionTitle>
                  <SectionDescription>
                    How many group members should be required to approve
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionRightColumn>
                  <div className="space-y-2">
                    <Select
                      value={groupApprovalCount}
                      onValueChange={setGroupApprovalCount}
                    >
                      <SelectTrigger
                        size="lg"
                        className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                      >
                        <SelectValue placeholder="Select a voting threshold" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          { length: selectedApprover.memberCount },
                          (_, i) => i + 1
                        ).map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} of {selectedApprover.memberCount} members
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </SectionRightColumn>
              </SectionRow>
            </>
          )}
      </Section>

      {/* Transaction Conditions */}
      <Section title="Transaction conditions">
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Transaction source accounts</SectionTitle>
            <SectionDescription>
              Only apply policy to transactions from these accounts
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <AccountSelector
              accounts={accountsForNetwork}
              selectedAccounts={selectedAccounts}
              onSelectionChange={setSelectedAccounts}
              placeholder="Select accounts (or leave empty for all accounts)"
              size="lg"
              className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
            />
          </SectionRightColumn>
        </SectionRow>

        <Separator className="w-full col-span-2" />
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Transaction initiator</SectionTitle>
            <SectionDescription>
              Only apply policy to transactions initiated by this person or
              group
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <MemberGroupSelector
              {...(selectedInitiator ? { value: selectedInitiator } : {})}
              onValueChange={(value) => setSelectedInitiator(value ?? null)}
              placeholder="Select a person or group"
              size="lg"
              className={`bg-background text-base ${outlineColor}`}
              members={members}
              groups={groups}
            />
          </SectionRightColumn>
        </SectionRow>

        <Separator className="w-full col-span-2" />
        <SectionRow>
          <SectionLeftColumn>
            <SectionTitle>Transaction type</SectionTitle>
            <SectionDescription>
              Only apply policy to transactions of this type
            </SectionDescription>
          </SectionLeftColumn>
          <SectionRightColumn>
            <div className="space-y-2">
              <Select
                value={policyTransactionType}
                onValueChange={(value) =>
                  setPolicyTransactionType(value as PolicyTransactionType)
                }
              >
                <SelectTrigger
                  id="transaction-type"
                  size="lg"
                  className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANY">Any type of transaction</SelectItem>
                  <SelectItem value="TOKEN_TRANSFER">
                    Token transfers
                  </SelectItem>
                  <SelectItem value="CONTRACT_INTERACTION">
                    Contract interactions
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </SectionRightColumn>
        </SectionRow>

        {/* Token Transfer Conditions */}
        {policyTransactionType === 'TOKEN_TRANSFER' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Token</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions sending this token
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <TokenSelector
                  tokens={tokenOptions}
                  isLoading={isLoadingTokens}
                  value={tokenSelection}
                  noBalanceTokenIds={
                    selectedTokenHasNoBalance && tokenSelection.token
                      ? [tokenSelection.token.id]
                      : []
                  }
                  onSelect={(token: OrganizationToken | null) => {
                    if (token) {
                      setTokenSelection({ type: 'specific', token });
                    } else {
                      setTokenSelection({ type: 'any' });
                    }
                  }}
                  placeholder="Any token"
                  size="lg"
                  className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                />
              </SectionRightColumn>
            </SectionRow>

            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Amount threshold</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions with this amount or less
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <div
                  className={`flex items-center bg-background rounded-md ${outlineColor}`}
                >
                  <span className="pl-3 text-muted-foreground select-none">
                    ≤
                  </span>
                  <Input
                    id="amount-threshold"
                    value={amountThreshold}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty or valid number with up to 6 decimal places
                      if (value === '' || /^\d*\.?\d{0,6}$/.test(value)) {
                        setAmountThreshold(value);
                      }
                    }}
                    placeholder="Any amount"
                    size="lg"
                    disabled={tokenSelection.type === 'any'}
                    className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              </SectionRightColumn>
            </SectionRow>

            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Transaction destination</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions sent to these addresses
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <DestinationAddressSelector
                  value={destinationAddressSelection}
                  onValueChange={setDestinationAddressSelection}
                />
              </SectionRightColumn>
            </SectionRow>
          </>
        )}

        {/* Contract Interaction Conditions */}
        {policyTransactionType === 'CONTRACT_INTERACTION' && (
          <>
            <Separator className="w-full col-span-2" />
            <SectionRow>
              <SectionLeftColumn>
                <SectionTitle>Contract</SectionTitle>
                <SectionDescription>
                  Only apply policy to transactions to this contract
                </SectionDescription>
              </SectionLeftColumn>
              <SectionRightColumn>
                <div className="space-y-1">
                  <ContractAddressSelector
                    value={contractAddressSelection}
                    onValueChange={setContractAddressSelection}
                    contractName={contractAbi.contractDisplayName}
                    isLoadingContract={contractAbi.isLoading}
                    status={
                      contractAbi.type === ContractAbiDetailsType.ERROR
                        ? 'warning'
                        : 'default'
                    }
                  />
                  {contractAbi.type === ContractAbiDetailsType.ERROR &&
                    contractAbi.errorMessage && (
                      <p className="text-sm text-red-500">
                        {contractAbi.errorMessage}
                      </p>
                    )}
                </div>
              </SectionRightColumn>
            </SectionRow>

            <Separator className="w-full col-span-2" />
            {contractAddressSelection.type === 'specific' && (
              <SectionRow>
                <SectionLeftColumn>
                  <SectionTitle>Functions</SectionTitle>
                  <SectionDescription>
                    Only apply policy to transactions calling these functions
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionRightColumn>
                  <FunctionSelector
                    value={functionSelection}
                    onValueChange={setFunctionSelection}
                    proxyFunctions={contractAbi.proxyFunctions}
                    implementationFunctions={
                      contractAbi.implementationFunctions
                    }
                    isLoading={contractAbi.isLoading}
                    error={contractAbi.error}
                    specificModeBlockedMessage={
                      contractAbi.type === ContractAbiDetailsType.ERROR &&
                      contractAbi.errorCode ===
                        ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT
                        ? 'This contract is unverified, so you cannot choose specific functions.'
                        : null
                    }
                    className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
                  />

                  {contractAbi.type === ContractAbiDetailsType.ERROR &&
                  contractAbi.errorCode !==
                    ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT ? (
                    <p className="text-sm text-muted-foreground mt-2">
                      Function selection is unavailable for this address.
                    </p>
                  ) : null}
                </SectionRightColumn>
              </SectionRow>
            )}
          </>
        )}
      </Section>

      {policyTransactionType === 'TOKEN_TRANSFER' ? (
        <Section title="Rate Limits">
          <SectionRow>
            <SectionLeftColumn>
              <SectionTitle>Rate Limits</SectionTitle>
              <SectionDescription>
                Control how often this policy can be used
              </SectionDescription>
            </SectionLeftColumn>
            <SectionRightColumn>
              <LimitationSelector
                value={limitation}
                onValueChange={setLimitation}
                placeholder="No limit"
                disabled={!canConfigureLimitation}
                disabledReason="Set an amount threshold to configure a limitation"
                className={`${sectionVariant === 'muted' ? 'bg-background' : ''} ${outlineColor}`}
              />
            </SectionRightColumn>
          </SectionRow>
        </Section>
      ) : null}
    </div>
  );
}
